import React, { useEffect, useState } from 'react';
import {
    Container,
    Typography,
    Grid,
    Button,
    Box,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Fab,
    Backdrop,
    LinearProgress,
    Snackbar,
    Alert,
    TextField,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Pagination,
    InputAdornment,
    FormControlLabel,
    Switch,
    Chip,
    OutlinedInput,
    Checkbox,
    ListItemText,
    IconButton
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import {
    getFlashBetSports,
    getCategoriesBySports,
    getMatchesWithFilters,
    deleteFlashBetMatch,
    toggleFlashBetMatchEnabled,
    createFlashBetMatch,
    updateFlashBetMatchMetadata,
    deleteFlashBetMatchQuestion,
    generateQuestionsForMatch,
    getFlashBetMatch,
    getMissingPossessionCount,
    backfillMissingPossession,
} from '../Api';
import { FlashBetMatchDto, CreateMatchRequest, UpdateMatchMetadataRequest, PageFlashBetMatchDto } from '../models/dtos/FlashBetMatchDto';
import FlashBetSportDto from '../models/dtos/FlashBetSportDto';
import FlashBetCategoryDto from '../models/dtos/FlashBetCategoryDto';
import AddMatchModal from '../components/AddMatchModal';
import EditMatchModal from '../components/EditMatchModal';
import MatchQuestionModal from '../components/MatchQuestionModal';
import EditScoreRecordsModal from '../components/EditScoreRecordsModal';
import EditMatchEventsModal from '../components/EditMatchEventsModal';
import FlashBetMatchCard from '../components/FlashBetMatchCard';
import PreviewPromptModal from '../components/PreviewPromptModal';
import { extractErrorMessage, createUserFriendlyErrorMessage } from '../utils/ErrorUtil';

const FlashBetMatchesPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    
    // Query parameter management
    const getQueryParams = () => {
        const params = new URLSearchParams(location.search);
        const sportIds = params.getAll('sportIds').map(Number).filter(id => !isNaN(id));
        const categoryIds = params.getAll('categoryIds').filter(id => id.length > 0);
        const showOnlyEnabled = params.get('showOnlyEnabled') === 'true';
        const search = params.get('search') || '';
        return { sportIds, categoryIds, showOnlyEnabled, search };
    };

    const hasQueryParams = () => {
        const params = new URLSearchParams(location.search);
        return params.toString().length > 0;
    };

    const updateQueryParams = (params: { sportIds: number[], categoryIds: string[], showOnlyEnabled: boolean, search: string }) => {
        const urlParams = new URLSearchParams();
        params.sportIds.forEach(id => urlParams.append('sportIds', id.toString()));
        params.categoryIds.forEach(id => urlParams.append('categoryIds', id));
        if (params.showOnlyEnabled) urlParams.set('showOnlyEnabled', 'true');
        if (params.search) urlParams.set('search', params.search);
        
        navigate(`${location.pathname}?${urlParams.toString()}`, { replace: true });
    };

    const queryParams = getQueryParams();
    
    // State management
    const [allSports, setAllSports] = useState<FlashBetSportDto[]>([]);
    const [availableCategories, setAvailableCategories] = useState<FlashBetCategoryDto[]>([]);
    const [selectedSportIds, setSelectedSportIds] = useState<number[]>(queryParams.sportIds);
    const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>(queryParams.categoryIds);
    const [showOnlyEnabled, setShowOnlyEnabled] = useState<boolean>(queryParams.showOnlyEnabled);
    const [searchTerm, setSearchTerm] = useState<string>(queryParams.search);
    const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
    const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
    const [allCollapsed, setAllCollapsed] = useState<boolean>(true);
    
    const [matchesPage, setMatchesPage] = useState<PageFlashBetMatchDto | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [matchesLoading, setMatchesLoading] = useState<boolean>(false);
    const [currentPage, setCurrentPage] = useState<number>(0);
    const [pageSize, setPageSize] = useState<number>(10);
    
    // Modal states
    const [addModalOpen, setAddModalOpen] = useState<boolean>(false);
    const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
    const [questionModalOpen, setQuestionModalOpen] = useState<boolean>(false);
    const [selectedMatch, setSelectedMatch] = useState<FlashBetMatchDto | null>(null);
    const [selectedQuestion, setSelectedQuestion] = useState<{ matchId: string; questionId?: string } | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
    const [matchToDelete, setMatchToDelete] = useState<FlashBetMatchDto | null>(null);
    const [scoreRecordsModalOpen, setScoreRecordsModalOpen] = useState<boolean>(false);
    const [selectedMatchForScores, setSelectedMatchForScores] = useState<string | null>(null);
    const [eventsModalOpen, setEventsModalOpen] = useState<boolean>(false);
    const [selectedMatchForEvents, setSelectedMatchForEvents] = useState<string | null>(null);
    const [selectedMatchForEventsData, setSelectedMatchForEventsData] = useState<FlashBetMatchDto | null>(null);
    const [copiedBetsApiId, setCopiedBetsApiId] = useState<string | null>(null);
    const [generatingQuestions, setGeneratingQuestions] = useState<string | null>(null);
    const [previewPromptModalOpen, setPreviewPromptModalOpen] = useState<boolean>(false);
    const [selectedMatchForPreview, setSelectedMatchForPreview] = useState<FlashBetMatchDto | null>(null);
    
    // Snackbar state
    const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
    const [snackbarMessage, setSnackbarMessage] = useState<string>('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
    const [missingPossessionCount, setMissingPossessionCount] = useState<number>(0);
    const [possessionBackfillLoading, setPossessionBackfillLoading] = useState<boolean>(false);

    const refreshMissingPossessionCount = async () => {
        try {
            const count = await getMissingPossessionCount(true);
            setMissingPossessionCount(count);
        } catch {
            setMissingPossessionCount(0);
        }
    };

    const handleBackfillPossession = async () => {
        setPossessionBackfillLoading(true);
        try {
            const result = await backfillMissingPossession(50, true);
            if (!result) {
                throw new Error('Backfill request failed');
            }
            setMissingPossessionCount(result.remaining);
            setSnackbarMessage(
                `Possession backfill: updated ${result.updated}, skipped ${result.skipped}, failed ${result.failed}. ` +
                `${result.remaining} match(es) still missing.`,
            );
            setSnackbarSeverity(result.failed > 0 ? 'error' : 'success');
            setSnackbarOpen(true);
            if (result.updated > 0) {
                await loadMatches();
            }
        } catch (error) {
            const errorMsg = await createUserFriendlyErrorMessage('backfill possession', error);
            setSnackbarMessage(errorMsg);
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setPossessionBackfillLoading(false);
        }
    };

    // Load initial data
    useEffect(() => {
        loadInitialData();
        refreshMissingPossessionCount();
    }, []);

    // Update query parameters when filters change
    useEffect(() => {
        updateQueryParams({ 
            sportIds: selectedSportIds, 
            categoryIds: selectedCategoryIds, 
            showOnlyEnabled, 
            search: searchTerm 
        });
    }, [selectedSportIds, selectedCategoryIds, showOnlyEnabled, searchTerm]);

    // Load categories when selected sports change
    useEffect(() => {
        loadCategoriesForSports();
    }, [selectedSportIds, showOnlyEnabled]);

    // Load matches when filters or pagination change
    useEffect(() => {
        loadMatches();
    }, [selectedSportIds, selectedCategoryIds, showOnlyEnabled, searchTerm, currentPage, pageSize]);

    // Cleanup search timeout
    useEffect(() => {
        return () => {
            if (searchTimeout) {
                clearTimeout(searchTimeout);
            }
        };
    }, [searchTimeout]);

    const loadInitialData = async () => {
        try {
            setLoading(true);
            const sportsData = await getFlashBetSports();
            setAllSports(sportsData);
            
            // Check if we should auto-select all items (no query params present)
            const shouldAutoSelect = !hasQueryParams();
            
            // Auto-select all sports if no query params
            if (shouldAutoSelect) {
                const availableSportIds = showOnlyEnabled ? 
                    sportsData.filter(sport => sport.enabled !== false).map(sport => sport.id) :
                    sportsData.map(sport => sport.id);
                setSelectedSportIds(availableSportIds);
            }
            
            // Load categories - either for selected sports from query params or for all sports
            const sportIds = shouldAutoSelect ? 
                (showOnlyEnabled ? 
                    sportsData.filter(sport => sport.enabled !== false).map(sport => sport.id) :
                    sportsData.map(sport => sport.id)) :
                (queryParams.sportIds.length > 0 ? queryParams.sportIds : sportsData.map(sport => sport.id));
                
            if (sportIds.length > 0) {
                const categoriesData = await getCategoriesBySports(sportIds);
                setAvailableCategories(categoriesData);
                
                // Auto-select all categories if no query params
                if (shouldAutoSelect) {
                    const availableCategoryIds = showOnlyEnabled ?
                        categoriesData.filter(category => category.enabled !== false).map(category => category.id) :
                        categoriesData.map(category => category.id);
                    setSelectedCategoryIds(availableCategoryIds);
                }
            }
            
            setIsInitialLoad(false);
        } catch (error) {
            console.error('Failed to load initial data:', error);
            const errorMsg = await extractErrorMessage(error, 'Failed to load sports data');
            setSnackbarMessage(errorMsg);
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setLoading(false);
        }
    };

    const loadCategoriesForSports = async () => {
        try {
            // When no sports are selected, treat it as "All" sports
            const sportIds = selectedSportIds.length > 0 ? selectedSportIds : allSports.map(sport => sport.id);
            
            if (sportIds.length === 0) {
                // If there are no sports at all, clear categories
                setAvailableCategories([]);
                setSelectedCategoryIds([]);
                return;
            }
            
            const categoriesData = await getCategoriesBySports(sportIds);
            const filteredCategories = showOnlyEnabled ? 
                categoriesData.filter(cat => cat.enabled) : 
                categoriesData;
            setAvailableCategories(filteredCategories);
            
            // Auto-select all categories if this is after initial load and no query params were present originally
            if (!isInitialLoad && !hasQueryParams() && selectedSportIds.length === allSports.length) {
                const availableCategoryIds = filteredCategories.map(cat => cat.id);
                setSelectedCategoryIds(availableCategoryIds);
            } else {
                // Remove selected categories that are no longer available
                const availableCategoryIds = filteredCategories.map(cat => cat.id);
                setSelectedCategoryIds(prev => prev.filter(id => availableCategoryIds.includes(id)));
            }
        } catch (error) {
            console.error('Failed to load categories:', error);
            const errorMsg = await extractErrorMessage(error, 'Failed to load categories');
            setSnackbarMessage(errorMsg);
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    const loadMatches = async () => {
        try {
            setMatchesLoading(true);
            
            const filteredSportIds = showOnlyEnabled ? 
                selectedSportIds.filter(id => {
                    const sport = allSports.find(s => s.id === id);
                    return sport?.enabled !== false;
                }) : 
                selectedSportIds;

            const filteredCategoryIds = showOnlyEnabled ? 
                selectedCategoryIds.filter(id => {
                    const category = availableCategories.find(c => c.id === id);
                    return category?.enabled !== false;
                }) : 
                selectedCategoryIds;

            const matchesData = await getMatchesWithFilters(
                filteredSportIds,
                filteredCategoryIds,
                searchTerm || undefined,
                showOnlyEnabled,
                currentPage,
                pageSize
            );
            setMatchesPage(matchesData);
        } catch (error) {
            console.error('Failed to load matches:', error);
            const errorMsg = await extractErrorMessage(error, 'Failed to load matches');
            setSnackbarMessage(errorMsg);
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setMatchesLoading(false);
        }
    };

    const reloadSpecificMatch = async (matchId: string) => {
        try {
            const updatedMatch = await getFlashBetMatch(matchId);
            if (updatedMatch && matchesPage) {
                setMatchesPage(prevPage => {
                    if (!prevPage) return prevPage;
                    return {
                        ...prevPage,
                        content: prevPage.content.map(m => m.id === matchId ? updatedMatch : m)
                    };
                });
            }
        } catch (error) {
            console.error('Failed to reload specific match:', error);
            const errorMsg = await extractErrorMessage(error, 'Failed to reload match');
            setSnackbarMessage(errorMsg);
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    const handleSportsChange = (event: any) => {
        const value = event.target.value as number[];
        setSelectedSportIds(value);
        setCurrentPage(0); // Reset to first page
    };

    const handleCategoriesChange = (event: any) => {
        const value = event.target.value as string[];
        setSelectedCategoryIds(value);
        setCurrentPage(0); // Reset to first page
    };

    const handleToggleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setShowOnlyEnabled(event.target.checked);
        setCurrentPage(0); // Reset to first page
    };

    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        setCurrentPage(value - 1);
    };

    const handlePageSizeChange = (event: any) => {
        const newSize = event.target.value as number;
        setPageSize(newSize);
        setCurrentPage(0); // Reset to first page when changing page size
    };

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        
        // Clear existing timeout
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }
        
        // Set new timeout for debounced search
        const timeout = setTimeout(() => {
            setSearchTerm(value);
            setCurrentPage(0); // Reset to first page when searching
        }, 500);
        
        setSearchTimeout(timeout);
    };

    const handleClearSearch = () => {
        // Clear existing timeout
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }
        
        setSearchTerm('');
        setCurrentPage(0); // Reset to first page when clearing search
    };

    const handleSelectAllSports = () => {
        const availableSportIds = showOnlyEnabled ? 
            allSports.filter(sport => sport.enabled !== false).map(sport => sport.id) :
            allSports.map(sport => sport.id);
        setSelectedSportIds(availableSportIds);
        setCurrentPage(0); // Reset to first page
    };

    const handleSelectAllCategories = () => {
        const availableCategoryIds = showOnlyEnabled ?
            availableCategories.filter(category => category.enabled !== false).map(category => category.id) :
            availableCategories.map(category => category.id);
        setSelectedCategoryIds(availableCategoryIds);
        setCurrentPage(0); // Reset to first page
    };

    const isSelectAllSportsDisabled = () => {
        const availableSportIds = showOnlyEnabled ? 
            allSports.filter(sport => sport.enabled !== false).map(sport => sport.id) :
            allSports.map(sport => sport.id);
        return availableSportIds.length === 0 || selectedSportIds.length === availableSportIds.length;
    };

    const isSelectAllCategoriesDisabled = () => {
        const availableCategoryIds = showOnlyEnabled ?
            availableCategories.filter(category => category.enabled !== false).map(category => category.id) :
            availableCategories.map(category => category.id);
        return availableCategoryIds.length === 0 || selectedCategoryIds.length === availableCategoryIds.length;
    };

    const handleResetSports = () => {
        setSelectedSportIds([]);
        setCurrentPage(0); // Reset to first page
    };

    const handleResetCategories = () => {
        setSelectedCategoryIds([]);
        setCurrentPage(0); // Reset to first page
    };

    const isResetSportsDisabled = () => {
        return selectedSportIds.length === 0;
    };

    const isResetCategoriesDisabled = () => {
        return selectedCategoryIds.length === 0;
    };

    const getSelectedSportsDisplay = () => {
        if (selectedSportIds.length === 0) return 'All Sports';
        return selectedSportIds.map(id => {
            const sport = allSports.find(s => s.id === id);
            return sport?.name || id.toString();
        }).join(', ');
    };

    const getSelectedCategoriesDisplay = () => {
        if (selectedCategoryIds.length === 0) return 'All Categories';
        return selectedCategoryIds.map(id => {
            const category = availableCategories.find(c => c.id === id);
            return category?.name || id;
        }).join(', ');
    };

    const handleAddMatch = async (data: CreateMatchRequest) => {
        const newMatch = await createFlashBetMatch(data);
        if (newMatch) {
            // Reload matches to get updated list
            await loadMatches();
            setAddModalOpen(false);
            
            // Open EditMatchModal for the newly created match
            setSelectedMatch(newMatch);
            setEditModalOpen(true);
            
            setSnackbarMessage('Match added successfully!');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
        } else throw new Error('API returned null - creation failed');
    };

    const handleEditMatch = (match: FlashBetMatchDto) => {
        setSelectedMatch(match);
        setEditModalOpen(true);
    };

    const handleEditScoreRecords = (match: FlashBetMatchDto) => {
        setSelectedMatchForScores(match.id);
        setScoreRecordsModalOpen(true);
    };

    const handleEditEvents = (match: FlashBetMatchDto) => {
        setSelectedMatchForEvents(match.id);
        setSelectedMatchForEventsData(match);
        setEventsModalOpen(true);
    };

    const handleSaveMatch = async (data: UpdateMatchMetadataRequest) => {
        if (!selectedMatch) return;

        const updatedMatch = await updateFlashBetMatchMetadata(selectedMatch.id, data);
        if (!updatedMatch) {
            throw new Error('API returned null - update failed');
        }

        // Update the specific match in the matches array instead of reloading all data
        setMatchesPage(prevPage => {
            if (!prevPage) return prevPage;
            return {
                ...prevPage,
                content: prevPage.content.map(m => m.id === updatedMatch.id ? updatedMatch : m)
            };
        });
        setEditModalOpen(false);
        setSelectedMatch(null);
    };

    const handleApplyChanges = async (data: UpdateMatchMetadataRequest) => {
        if (!selectedMatch) return;

        const updatedMatch = await updateFlashBetMatchMetadata(selectedMatch.id, data);
        if (!updatedMatch) {
            throw new Error('API returned null - update failed');
        }

        // Update the specific match in the matches array instead of reloading all data
        setMatchesPage(prevPage => {
            if (!prevPage) return prevPage;
            return {
                ...prevPage,
                content: prevPage.content.map(m => m.id === updatedMatch.id ? updatedMatch : m)
            };
        });
        setSelectedMatch(updatedMatch); // Keep modal open with updated data
    };

    const handleDeleteMatch = async () => {
        if (!matchToDelete) return;

        try {
            const success = await deleteFlashBetMatch(matchToDelete.id);
            if (success) {
                await loadMatches();
            }
        } catch (error) {
            console.error('Failed to delete match:', error);
            const errorMsg = await createUserFriendlyErrorMessage('delete match', error);
            setSnackbarMessage(errorMsg);
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setDeleteDialogOpen(false);
            setMatchToDelete(null);
        }
    };

    const handleToggleMatchEnabled = async (match: FlashBetMatchDto) => {
        try {
            const updatedMatch = await toggleFlashBetMatchEnabled(match.id);
            if (updatedMatch) {
                await loadMatches();
            }
        } catch (error) {
            console.error('Failed to toggle match enabled:', error);
            const errorMsg = await createUserFriendlyErrorMessage('toggle match enabled status', error);
            setSnackbarMessage(errorMsg);
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    const handleAddQuestion = (match: FlashBetMatchDto) => {
        setSelectedQuestion({ matchId: match.id });
        setQuestionModalOpen(true);
    };

    const handleEditQuestion = (match: FlashBetMatchDto, questionId: string) => {
        setSelectedQuestion({ matchId: match.id, questionId });
        setQuestionModalOpen(true);
    };

    const handleDeleteQuestion = async (matchId: string, questionId: string) => {
        try {
            const updatedMatch = await deleteFlashBetMatchQuestion(matchId, questionId);
            if (updatedMatch) {
                // Update only the specific match instead of reloading all data
                setMatchesPage(prevPage => {
                    if (!prevPage) return prevPage;
                    return {
                        ...prevPage,
                        content: prevPage.content.map(m => m.id === matchId ? updatedMatch : m)
                    };
                });
            }
        } catch (error) {
            console.error('Failed to delete question:', error);
            const errorMsg = await createUserFriendlyErrorMessage('delete question', error);
            setSnackbarMessage(errorMsg);
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    const handleGenerateQuestions = async (match: FlashBetMatchDto, promptId?: string) => {
        try {
            setGeneratingQuestions(match.id);
            const success = await generateQuestionsForMatch(match.id, promptId);
            
            if (success) {
                // Reload the specific match to get updated questions
                const updatedMatch = await getFlashBetMatch(match.id);
                if (updatedMatch) {
                    // Update the match in the matches array
                    setMatchesPage(prevPage => {
                        if (!prevPage) return prevPage;
                        return {
                            ...prevPage,
                            content: prevPage.content.map(m => m.id === match.id ? updatedMatch : m)
                        };
                    });
                }
                setSnackbarMessage('Questions generated successfully!');
                setSnackbarSeverity('success');
                setSnackbarOpen(true);
            } else {
                setSnackbarMessage('Failed to generate questions. Please try again.');
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
            }
        } catch (error) {
            console.error('Failed to generate questions:', error);
            const errorMsg = await createUserFriendlyErrorMessage('generate questions', error);
            setSnackbarMessage(errorMsg);
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setGeneratingQuestions(null);
        }
    };

    const handlePreviewAndGenerate = (match: FlashBetMatchDto) => {
        setSelectedMatchForPreview(match);
        setPreviewPromptModalOpen(true);
    };

    const handlePreviewPromptGenerate = async (promptId?: string) => {
        if (selectedMatchForPreview) {
            await handleGenerateQuestions(selectedMatchForPreview, promptId);
        }
    };

    const handleQuestionSaved = async () => {
        setQuestionModalOpen(false);
        
        // Reload only the specific match instead of all data
        if (selectedQuestion?.matchId) {
            await reloadSpecificMatch(selectedQuestion.matchId);
        }
        
        setSelectedQuestion(null);
    };

    const handleCopyBetsApiId = async (betsApiId: string) => {
        try {
            await navigator.clipboard.writeText(betsApiId);
            setCopiedBetsApiId(betsApiId);
            setTimeout(() => setCopiedBetsApiId(null), 2000); // Clear after 2 seconds
        } catch (error) {
            console.error('Failed to copy betsApiId:', error);
            const errorMsg = await extractErrorMessage(error, 'Failed to copy to clipboard');
            setSnackbarMessage(errorMsg);
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString();
    };

    if (loading) {
        return (
            <Container>
                <Box display="flex" justifyContent="center" mt={4}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Box display="flex" alignItems="center" mb={3}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/flashbet/sports')}
                    sx={{ mr: 2 }}
                >
                    Back to Sports
                </Button>
                <Typography variant="h4" component="h1" sx={{ flexGrow: 1 }}>
                    FlashBet Matches
                </Typography>
                
                {/* Page Size Selector */}
                <FormControl size="small" sx={{ minWidth: 120, mr: 2 }}>
                    <InputLabel>Page Size</InputLabel>
                    <Select
                        value={pageSize}
                        label="Page Size"
                        onChange={handlePageSizeChange}
                        disabled={matchesLoading}
                    >
                        <MenuItem value={5}>5</MenuItem>
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={20}>20</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            {/* Filters */}
            <Box mb={3}>
                <Grid container spacing={2} alignItems="center">
                    {/* Sports Multi-Select */}
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                            <InputLabel>Sports</InputLabel>
                            <Select
                                multiple
                                value={selectedSportIds}
                                onChange={handleSportsChange}
                                input={<OutlinedInput label="Sports" />}
                                renderValue={() => getSelectedSportsDisplay()}
                            >
                                {allSports.map((sport) => (
                                    <MenuItem key={sport.id} value={sport.id}>
                                        <Checkbox checked={selectedSportIds.includes(sport.id)} />
                                        <ListItemText primary={sport.name} />
                                        {!sport.enabled && <Chip label="Disabled" size="small" color="warning" />}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Box mt={1}>
                            <Button
                                size="small"
                                variant="text"
                                onClick={handleSelectAllSports}
                                disabled={isSelectAllSportsDisabled()}
                                sx={{ textTransform: 'none', fontSize: '0.75rem', mr: 1 }}
                            >
                                Select All
                            </Button>
                            <Button
                                size="small"
                                variant="text"
                                onClick={handleResetSports}
                                disabled={isResetSportsDisabled()}
                                sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                            >
                                Reset
                            </Button>
                        </Box>
                    </Grid>

                    {/* Categories Multi-Select */}
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                            <InputLabel>Categories</InputLabel>
                            <Select
                                multiple
                                value={selectedCategoryIds}
                                onChange={handleCategoriesChange}
                                input={<OutlinedInput label="Categories" />}
                                renderValue={() => getSelectedCategoriesDisplay()}
                            >
                                {availableCategories.map((category) => (
                                    <MenuItem key={category.id} value={category.id}>
                                        <Checkbox checked={selectedCategoryIds.includes(category.id)} />
                                        <ListItemText primary={category.name} />
                                        {!category.enabled && <Chip label="Disabled" size="small" color="warning" />}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Box mt={1}>
                            <Button
                                size="small"
                                variant="text"
                                onClick={handleSelectAllCategories}
                                disabled={isSelectAllCategoriesDisabled()}
                                sx={{ textTransform: 'none', fontSize: '0.75rem', mr: 1 }}
                            >
                                Select All
                            </Button>
                            <Button
                                size="small"
                                variant="text"
                                onClick={handleResetCategories}
                                disabled={isResetCategoriesDisabled()}
                                sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                            >
                                Reset
                            </Button>
                        </Box>
                    </Grid>

                    {/* Show Only Enabled Toggle */}
                    <Grid item xs={12} md={4}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={showOnlyEnabled}
                                    onChange={handleToggleChange}
                                />
                            }
                            label="Show only enabled"
                        />
                    </Grid>
                </Grid>
            </Box>

            {/* Search Field and Collapse/Expand Button */}
            <Box mb={3} display="flex" gap={2} alignItems="flex-start">
                <TextField
                    fullWidth
                    label="Search matches..."
                    variant="outlined"
                    defaultValue={searchTerm}
                    onChange={handleSearchChange}
                    placeholder="Enter match name, team, or ID to search"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                {matchesLoading ? (
                                    <CircularProgress size={20} />
                                ) : (
                                    <SearchIcon />
                                )}
                            </InputAdornment>
                        ),
                        endAdornment: searchTerm ? (
                            <InputAdornment position="end">
                                <IconButton
                                    size="small"
                                    onClick={handleClearSearch}
                                    edge="end"
                                    aria-label="clear search"
                                >
                                    <ClearIcon />
                                </IconButton>
                            </InputAdornment>
                        ) : null,
                    }}
                />
                <Button
                    variant="outlined"
                    onClick={() => setAllCollapsed(!allCollapsed)}
                    sx={{
                        textTransform: 'none',
                        fontSize: '0.875rem',
                        px: 2,
                        py: 1.75,
                        minWidth: '140px',
                        whiteSpace: 'nowrap'
                    }}
                >
                    {allCollapsed ? 'Expand All' : 'Collapse All'}
                </Button>
                {missingPossessionCount > 0 && (
                    <Button
                        variant="contained"
                        color="secondary"
                        disabled={possessionBackfillLoading}
                        onClick={handleBackfillPossession}
                        sx={{
                            textTransform: 'none',
                            fontSize: '0.875rem',
                            px: 2,
                            py: 1.75,
                            minWidth: '200px',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        {possessionBackfillLoading ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : (
                            `Backfill possession (${missingPossessionCount})`
                        )}
                    </Button>
                )}
            </Box>

            {/* Pagination Top */}
            {matchesPage && matchesPage.totalPages > 1 && (
                <Box display="flex" justifyContent="center" mb={3}>
                    <Pagination
                        count={matchesPage.totalPages}
                        page={currentPage + 1}
                        onChange={handlePageChange}
                        color="primary"
                        size="large"
                        disabled={matchesLoading}
                    />
                </Box>
            )}

            <Grid container spacing={3}>
                {matchesLoading ? (
                    <Grid item xs={12}>
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                            <CircularProgress />
                        </Box>
                    </Grid>
                ) : (
                    matchesPage?.content.map((match) => (
                        <Grid item xs={12} key={match.id}>
                            <FlashBetMatchCard
                                match={match}
                                onEdit={handleEditMatch}
                                onEditScoreRecords={handleEditScoreRecords}
                                onEditEvents={handleEditEvents}
                                onDelete={(match) => {
                                    setMatchToDelete(match);
                                    setDeleteDialogOpen(true);
                                }}
                                onAddQuestion={handleAddQuestion}
                                onEditQuestion={handleEditQuestion}
                                onDeleteQuestion={handleDeleteQuestion}
                                onGenerateQuestions={handleGenerateQuestions}
                                onPreviewAndGenerate={handlePreviewAndGenerate}
                                onCopyBetsApiId={handleCopyBetsApiId}
                                onShowError={(message) => {
                                    setSnackbarMessage(message);
                                    setSnackbarSeverity('error');
                                    setSnackbarOpen(true);
                                }}
                                generatingQuestions={generatingQuestions}
                                copiedBetsApiId={copiedBetsApiId}
                                defaultCollapsed={allCollapsed}
                            />
                        </Grid>
                    ))
                )}
            </Grid>

            {/* No matches message */}
            {!matchesLoading && matchesPage?.content.length === 0 && (
                <Box display="flex" flexDirection="column" alignItems="center" mt={4} mb={4}>
                    <Typography variant="h6" color="text.secondary">
                        {searchTerm ? 'No matches found for your search' : 'No matches available'}
                    </Typography>
                    {searchTerm && (
                        <Typography variant="body2" color="text.secondary" mt={1}>
                            Try adjusting your search terms
                        </Typography>
                    )}
                </Box>
            )}

            {/* Pagination Bottom */}
            {matchesPage && matchesPage.totalPages > 1 && (
                <Box display="flex" justifyContent="center" mt={3} mb={4}>
                    <Pagination
                        count={matchesPage.totalPages}
                        page={currentPage + 1}
                        onChange={handlePageChange}
                        color="primary"
                        size="large"
                        disabled={matchesLoading}
                    />
                </Box>
            )}

            {/* Add some spacing at the bottom */}
            <Box sx={{ mb: 10 }} />

            <Fab
                color="primary"
                aria-label="add"
                sx={{ position: 'fixed', bottom: 16, right: 16 }}
                onClick={() => setAddModalOpen(true)}
            >
                <AddIcon />
            </Fab>

            {/* Add Match Modal */}
            <AddMatchModal
                open={addModalOpen}
                categoryId={selectedCategoryIds.length > 0 ? selectedCategoryIds[0] : ''}
                onSave={handleAddMatch}
                onClose={() => setAddModalOpen(false)}
            />

            {/* Edit Match Modal */}
            <EditMatchModal
                open={editModalOpen}
                match={selectedMatch}
                onSave={handleSaveMatch}
                onApply={handleApplyChanges}
                onDelete={async (matchId: string) => {
                    const matchToDelete = matchesPage?.content.find(m => m.id === matchId);
                    if (matchToDelete) {
                        setMatchToDelete(matchToDelete);
                        setDeleteDialogOpen(true);
                    }
                }}
                onRefetch={(updatedMatch: FlashBetMatchDto) => {
                    // Update the specific match in the matches array
                    setMatchesPage(prevPage => {
                        if (!prevPage) return prevPage;
                        return {
                            ...prevPage,
                            content: prevPage.content.map(m => m.id === updatedMatch.id ? updatedMatch : m)
                        };
                    });
                    // Update the selected match as well so the modal shows updated data
                    setSelectedMatch(updatedMatch);
                }}
                onClose={() => {
                    setEditModalOpen(false);
                    setSelectedMatch(null);
                }}
            />

            {/* Match Question Modal */}
            <MatchQuestionModal
                open={questionModalOpen}
                matchId={selectedQuestion?.matchId || ''}
                questionId={selectedQuestion?.questionId}
                matchEnabled={matchesPage?.content.find(m => m.id === selectedQuestion?.matchId)?.enabled || false}
                onSave={handleQuestionSaved}
                onClose={() => {
                    setQuestionModalOpen(false);
                    setSelectedQuestion(null);
                }}
            />

            {/* Edit Score Records Modal */}
            <EditScoreRecordsModal
                open={scoreRecordsModalOpen}
                matchId={selectedMatchForScores}
                onClose={async () => {
                    setScoreRecordsModalOpen(false);
                    // Reload only the specific match to get updated score records count
                    if (selectedMatchForScores) {
                        await reloadSpecificMatch(selectedMatchForScores);
                    }
                    setSelectedMatchForScores(null);
                }}
            />

            {/* Edit Match Events Modal */}
            <EditMatchEventsModal
                open={eventsModalOpen}
                matchId={selectedMatchForEvents || ''}
                match={selectedMatchForEventsData}
                onClose={() => {
                    setEventsModalOpen(false);
                    setSelectedMatchForEvents(null);
                    setSelectedMatchForEventsData(null);
                }}
                onSave={async () => {
                    // Reload only the specific match instead of all data
                    if (selectedMatchForEvents) {
                        await reloadSpecificMatch(selectedMatchForEvents);
                    }
                }}
                onRefetch={async () => {
                    // Reload only the specific match instead of all data
                    if (selectedMatchForEvents) {
                        await reloadSpecificMatch(selectedMatchForEvents);
                    }
                }}
            />

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    Are you sure you want to delete this match? This action cannot be undone.
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleDeleteMatch} color="error">Delete</Button>
                </DialogActions>
            </Dialog>

            {/* Loading Overlay for Question Generation */}
            <Backdrop
                open={generatingQuestions !== null}
                sx={{ 
                    color: '#fff', 
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    flexDirection: 'column',
                    gap: 2
                }}
            >
                <CircularProgress color="inherit" size={60} />
                <Typography variant="h6">Generating questions with AI...</Typography>
                <LinearProgress 
                    sx={{ 
                        width: '300px',
                        '& .MuiLinearProgress-bar': {
                            background: 'linear-gradient(45deg, #FF6B6B, #4ECDC4, #45B7D1)'
                        }
                    }} 
                />
                <Typography variant="body2" sx={{ textAlign: 'center', maxWidth: '400px' }}>
                    Please wait while we analyze the match data and generate relevant questions...
                </Typography>
            </Backdrop>

            {/* Preview Prompt Modal */}
            <PreviewPromptModal
                open={previewPromptModalOpen}
                matchId={selectedMatchForPreview?.id || ''}
                sportId={selectedMatchForPreview?.sport?.id || 0}
                onClose={() => {
                    setPreviewPromptModalOpen(false);
                    setSelectedMatchForPreview(null);
                }}
                onGenerate={handlePreviewPromptGenerate}
                onShowError={(message) => {
                    setSnackbarMessage(message);
                    setSnackbarSeverity('error');
                    setSnackbarOpen(true);
                }}
            />

            {/* Success/Error Snackbar */}
            <Snackbar 
                open={snackbarOpen} 
                autoHideDuration={6000} 
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert 
                    onClose={() => setSnackbarOpen(false)} 
                    severity={snackbarSeverity}
                    sx={{ width: '100%' }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default FlashBetMatchesPage;